import React, {useLayoutEffect, useRef, useState} from "react";

import {Button, useMediaQuery} from "@mui/material";
import {Add} from "@vedmarket/ui-kit";

import OutlinedButton from "./../../components/buttons/Outlined";
import ContainedButton from "./../../components/buttons/Contained";
import BrowserPreviewImage from "./BrowserPreviewImage";
import CallToActionModal from "./CallToActionModal";

import styles from './CallToActionSection.module.scss';
import H4 from "../../components/typography/H4";
import P1 from "../../components/typography/P1";
import {COLORS} from "../../../../../config/constants";
import {typografText} from "../../utils/typograf";

type CallToActionSectionProps = {
    text: React.ReactNode;
};

function CallToActionSection({text}: CallToActionSectionProps) {

    const [open, setOpen] = useState(false);
    const isMobile = useMediaQuery('(max-width: 767px)');
    const isSmallMobile = useMediaQuery('(max-width: 575px)');

    const browserFrameRef = useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
        const frame = browserFrameRef.current;

        if (!isSmallMobile || !frame) {
            return;
        }

        let pixelOffset = 0;

        const alignBrowserToPixels = () => {
            // Fractional text heights above the preview otherwise blur its 1px divider.
            const layoutTop = frame.getBoundingClientRect().top + window.scrollY - pixelOffset;
            pixelOffset = Math.round(layoutTop) - layoutTop;
            frame.style.setProperty('--browser-pixel-offset', `${pixelOffset}px`);
        };

        const observer = new ResizeObserver(alignBrowserToPixels);
        observer.observe(document.documentElement);
        observer.observe(frame);
        window.addEventListener('resize', alignBrowserToPixels);
        alignBrowserToPixels();

        return () => {
            observer.disconnect();
            window.removeEventListener('resize', alignBrowserToPixels);
            frame.style.removeProperty('--browser-pixel-offset');
        };
    }, [isSmallMobile]);

    const handleOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    return (
        <>
            <div className={styles.section}>
                <div className={styles.sectionContainer}>
                    <div className={styles.leftBlock}>
                        <div className={styles.title}>
                            <H4 sx={{
                                display: 'block',
                                color: COLORS.WHITE,
                                minWidth: 0,
                                '@media (max-width: 575px)': {
                                    textWrap: 'wrap',
                                },
                                '@media (min-width: 1400px)': {
                                    position: 'relative',
                                    top: '8px',
                                    textWrap: 'balance',
                                },
                                '@media (min-width: 768px) and (max-width: 1199px)': {
                                    position: 'relative',
                                    top: '4px',
                                },
                                '@media (min-width: 576px) and (max-width: 767px)': {
                                    position: 'relative',
                                    top: '3px',
                                    overflowWrap: 'normal',
                                    wordBreak: 'normal',
                                },
                            }}>
                                {text}
                            </H4>
                        </div>
                        <div className={styles.buttonsBlock}>
                            <ContainedButton
                                icon={isMobile ? null : 'handshake'}
                                isWhite
                                mobileSpacing="compact"
                            >
                                Become a partner
                            </ContainedButton>
                            <OutlinedButton
                                icon={isMobile ? null : 'play'}
                                isBlack
                                mobileSpacing="compact"
                                subduedMobileBorder
                            >
                                Watch demo
                            </OutlinedButton>
                        </div>
                    </div>
                    <div className={styles.rightBlock}>
                        <div className={styles.imageCaption}>
                            <P1 sx={{
                                color: COLORS.WHITE,
                                fontStyle: 'italic',
                                '@media (max-width: 575px)': {
                                    fontFamily: '"Partners Test Roboto", Roboto, sans-serif',
                                },
                            }}>
                                Example: finding a shipping option
                            </P1>
                        </div>
                        <div className={styles.browserFrame} ref={browserFrameRef}>
                            <div className={styles.browserHeader}>
                                <span className={styles.browserControl}></span>
                                <span className={styles.browserControl}></span>
                                <span className={styles.browserControl}></span>
                            </div>
                            <div className={styles.browserContent} onClick={handleOpen}>
                                <Button
                                    variant="contained"
                                    sx={{
                                        letterSpacing: '0.46px',
                                        padding: '7px 18px',
                                        fontWeight: 600,
                                        border: `1px solid ${COLORS.PRIMARY_LIGHT}`,
                                        background: COLORS.WHITE,
                                        fontSize: '15px',
                                        fontStyle: 'normal',
                                        lineHeight: 1.7,
                                        textTransform: 'uppercase',
                                        color: COLORS.PRIMARY,
                                        position: 'absolute',
                                    }}
                                    className={styles.zoomButton}
                                    onClick={handleOpen}>
                                    <Add className={styles.zoomIcon} aria-hidden="true" focusable="false"/>
                                    {typografText('Enlarge')}
                                </Button>
                                <BrowserPreviewImage
                                    className={styles.browserImage}
                                    sizes="(max-width: 767px) calc(100vw - 64px), (max-width: 1199px) calc(100vw - 96px), min(944px, calc(100vw - 128px))"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {open && <CallToActionModal open={open} handleClose={handleClose}/>}
        </>
    );
}

export default CallToActionSection
